// Regaletiketten: Vormerkliste je Standort (füllt sich, wenn sich ein Regalpreis ändert, und von Hand) und das
// Druckbild. Ein Etikett zeigt nach PAngV den Gesamtpreis, den Grundpreis je Liter bzw. Kilogramm und das Pfand
// GETRENNT – das Pfand ist nie Teil des Gesamtpreises.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zDay, zLocationRef, zUnitRef } from './common'

const PRINT = 'pricing.label.print'

export const LABEL_REASONS = ['manual', 'price_change', 'new_item', 'promotion', 'receipt'] as const
export const LABEL_STATUSES = ['pending', 'printed', 'cancelled'] as const

export const zLabelRow = z.object({
  id: zId,
  location: zLocationRef,
  itemUnitId: zId,
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  unit: zUnitRef,
  reason: z.enum(LABEL_REASONS),
  status: z.enum(LABEL_STATUSES),
  copies: z.int(),
  requestedBy: z.string().nullable(),
  createdAt: z.date(),
  printedAt: z.date().nullable(),
})

export const labelList = defineContract({
  name: 'pricing.label.list',
  title: 'Etiketten-Vormerkliste anzeigen',
  description: 'Regaletiketten, die an einem Standort zu drucken sind – nach Preisänderungen, Aktionen oder von Hand vorgemerkt.',
  llm: {
    description:
      'Listet vorgemerkte Regaletiketten je Standort mit Grund (Preisänderung, Aktion, von Hand) und Zustand ' +
      '(pending = zu drucken, printed, cancelled). Nur Standorte, an denen die Person Etiketten drucken darf.',
    keywords: ['Etiketten', 'Regaletikett', 'Preisschild', 'Etiketten drucken', 'Vormerkliste'],
  },
  input: z.strictObject({
    locationId: zId.optional(),
    status: z.enum(LABEL_STATUSES).default('pending'),
    limit: z.int().min(1).max(500).default(300),
  }),
  output: z.object({ rows: z.array(zLabelRow), pendingByLocation: z.array(z.object({ location: zLocationRef, count: z.int() })) }),
  permission: PRINT,
  risk: 'read',
  scope: 'any',
})

export const labelAdd = defineContract({
  name: 'pricing.label.add',
  title: 'Etiketten vormerken',
  description: 'Merkt Regaletiketten für Artikel-Gebinde an einem Standort zum Druck vor.',
  llm: {
    description:
      'Merkt für einen Standort Regaletiketten vor: je Artikel das Gebinde (`unitId`, ohne Angabe das ' +
      'Verkaufsgebinde) und die Zahl der Etiketten. Ein bereits vorgemerktes Etikett wird nicht doppelt angelegt. ' +
      'Gedruckt wird in der Oberfläche unter „Etiketten“.',
    keywords: ['Etikett vormerken', 'Etikett drucken', 'Preisschild neu', 'Regaletikett fehlt'],
  },
  input: z.strictObject({
    locationId: zId,
    items: z
      .array(z.strictObject({ itemId: zId, unitId: zId.nullish(), copies: z.int().min(1).max(50).default(1) }))
      .min(1, { error: 'Bitte mindestens einen Artikel wählen.' })
      .max(200),
  }),
  output: z.object({ addedCount: z.int(), skippedCount: z.int() }),
  permission: PRINT,
  risk: 'write',
  scope: (input) => [input.locationId],
  limits: { maxItems: 200, itemCount: (input) => input.items.length },
})

export const zShelfLabel = z.object({
  /** Eintrag der Vormerkliste; null bei frei zusammengestellten Etiketten */
  queueId: zId.nullable(),
  itemUnitId: zId,
  sku: z.string(),
  name: z.string(),
  /** „Kasten 20 × 0,5 l“ */
  unitLabel: z.string(),
  /** Gesamtpreis brutto in Cent (ohne Pfand); null = kein Preis hinterlegt */
  priceCents: z.int().nullable(),
  /** „1,98 €/l“ – Pflichtangabe, wenn der Artikel sie verlangt */
  basePriceText: z.string().nullable(),
  /** Pfand je Gebinde in Cent, getrennt ausgewiesen; null = pfandfrei */
  depositCents: z.int().nullable(),
  /** „zzgl. 3,10 € Pfand“ */
  depositText: z.string().nullable(),
  isPromotion: z.boolean(),
  /** „Aktion 13.–19.10.“ */
  promotionText: z.string().nullable(),
  /** niedrigster Preis der letzten 30 Tage vor der Aktion (PAngV § 11), brutto */
  referenceCents: z.int().nullable(),
  barcode: z.string().nullable(),
  copies: z.int(),
})
export type ShelfLabel = z.output<typeof zShelfLabel>

export const labelSheet = defineContract({
  name: 'pricing.label.sheet',
  title: 'Etikettenbogen aufbereiten',
  description: 'Stellt die Etiketten eines Standorts für den Druck auf A4 zusammen – Preis und Grundpreis.',
  llm: {
    description:
      'Liefert das Druckbild der Regaletiketten eines Standorts: je Etikett Name, Gebinde, Bruttopreis aus der ' +
      'Preisfindung am Standort zum Preisstand `date` (Standard heute), Grundpreis je Liter/Kilogramm ' +
      'getrennt. Ohne `ids` alle vorgemerkten Etiketten des Standorts. Druckt nichts und hakt nichts ab – dafür ' +
      'pricing.label.markPrinted.',
    keywords: ['Etikettenbogen', 'Etiketten drucken', 'Druckvorschau'],
  },
  input: z.strictObject({ locationId: zId, ids: z.array(zId).max(500).optional(), date: zDay.optional() }),
  output: z.object({
    location: zLocationRef,
    date: z.string(),
    /** Demo-Betrieb: Etiketten tragen den Vermerk DEMO */
    demo: z.boolean(),
    priceGroupName: z.string().nullable(),
    labels: z.array(zShelfLabel),
  }),
  permission: PRINT,
  risk: 'read',
  scope: (input) => [input.locationId],
})

export const labelMarkPrinted = defineContract({
  name: 'pricing.label.markPrinted',
  title: 'Etiketten als gedruckt abhaken',
  description: 'Hakt vorgemerkte Etiketten als gedruckt ab.',
  llm: {
    description: 'Hakt vorgemerkte Etiketten eines Standorts als gedruckt ab (`ids` aus pricing.label.list). Ändert keine Preise.',
    keywords: ['Etiketten gedruckt', 'abhaken'],
  },
  input: z.strictObject({ locationId: zId, ids: z.array(zId).min(1).max(500) }),
  output: z.object({ printedCount: z.int() }),
  permission: PRINT,
  risk: 'write',
  scope: (input) => [input.locationId],
})

export const labelCancel = defineContract({
  name: 'pricing.label.cancel',
  title: 'Vorgemerkte Etiketten verwerfen',
  description: 'Nimmt vorgemerkte Etiketten aus der Vormerkliste, ohne sie zu drucken.',
  llm: {
    description: 'Verwirft vorgemerkte Etiketten eines Standorts (`ids` aus pricing.label.list), ohne sie zu drucken.',
    keywords: ['Etikett verwerfen', 'Vormerkung löschen'],
  },
  input: z.strictObject({ locationId: zId, ids: z.array(zId).min(1).max(500) }),
  output: z.object({ cancelledCount: z.int() }),
  permission: PRINT,
  risk: 'write',
  scope: (input) => [input.locationId],
})

/** Zahl der Etiketten, die an den Standorten der Person auf den Druck warten – für den Zähler in der Navigation. */
export const labelPendingCount = defineContract({
  name: 'pricing.label.pendingCount',
  title: 'Offene Etiketten zählen',
  description: 'Zählt die vorgemerkten Etiketten an den eigenen Standorten.',
  llm: { description: 'Zählt vorgemerkte, noch nicht gedruckte Etiketten an den Standorten der Person.', keywords: ['Etiketten offen'] },
  input: z.strictObject({ locationId: zId.optional() }),
  output: z.object({ count: z.int() }),
  permission: PRINT,
  risk: 'read',
  scope: 'any',
  assistant: 'never',
})
